// Monster and summon definitions. Stats are "per-level-1 base" values and are scaled
// by monsterHp() / monsterDmg() using the level of the zone they spawn in.

import { EffectDef, Element, MonsterAbility, MonsterDef, ProjVisual, StatusApply } from './types';

export function monsterHpScale(level: number): number {
  return 1 + 0.16 * level + 0.035 * level * level;
}
export function monsterDmgScale(level: number): number {
  return 1 + 0.22 * level + 0.012 * level * level;
}
export function monsterArmor(base: number, level: number): number {
  return Math.round(base * (1 + level * 0.25));
}
export function monsterXp(base: number, level: number): number {
  return Math.round(base * (8 + 2.6 * level));
}

type AbilityOpts = Partial<MonsterAbility>;

const bite = (mult = 1, o: AbilityOpts & { status?: StatusApply; el?: Element; vis?: string; reach?: number } = {}): MonsterAbility => {
  const { status, el, vis, reach, ...rest } = o;
  return {
    effects: [{ k: 'arc', range: reach ?? 1.6, angle: 110, mult, el: el ?? 'phys', status, vis: vis ?? 'clawHit' }],
    range: (reach ?? 1.6) - 0.15,
    cd: 1.1,
    cast: 0.3,
    recover: 0.3,
    weight: 3,
    anim: 'swing',
    sfx: 'bite',
    ...rest,
  };
};

const shot = (
  vis: ProjVisual,
  el: Element,
  speed: number,
  mult = 1,
  o: AbilityOpts & { status?: StatusApply; count?: number; spread?: number; explode?: { r: number; mult: number; el: Element; vis: string }; homing?: number; pierce?: number; ring?: boolean; lob?: boolean; life?: number; radius?: number } = {},
): MonsterAbility => {
  const { status, count, spread, explode, homing, pierce, ring, lob, life, radius, ...rest } = o;
  return {
    effects: [{ k: 'proj', proj: { vis, speed, life: life ?? 1.6, r: radius ?? 0.28, mult, el, status, count, spread, explode, homing, pierce, ring, lob } }],
    range: 9,
    cd: 2.0,
    cast: 0.4,
    recover: 0.3,
    weight: 3,
    anim: 'shoot',
    sfx: 'shootE',
    ...rest,
  };
};

const nova = (r: number, mult: number, el: Element, vis: string, o: AbilityOpts & { delay?: number; at?: 'self' | 'target'; status?: StatusApply; knock?: number; pull?: number } = {}): MonsterAbility => {
  const { delay, at, status, knock, pull, ...rest } = o;
  return {
    effects: [{ k: 'nova', r, mult, el, at: at ?? 'self', delay: delay ?? 0.7, status, vis, knock, pull }],
    range: at === 'target' ? 11 : r - 0.5,
    cd: 5,
    cast: 0.3,
    recover: 0.4,
    weight: 2,
    anim: 'slam',
    sfx: 'slam',
    ...rest,
  };
};

const groundPatch = (r: number, dur: number, mult: number, el: Element, vis: string, o: AbilityOpts & { status?: StatusApply } = {}): MonsterAbility => {
  const { status, ...rest } = o;
  return {
    effects: [{ k: 'ground', r, dur, every: 0.5, mult, el, at: 'target', status, vis }],
    range: 10,
    cd: 7,
    cast: 0.5,
    recover: 0.3,
    weight: 2,
    anim: 'cast',
    sfx: 'cast',
    ...rest,
  };
};

const summon = (monsters: string[], count: number, o: AbilityOpts = {}): MonsterAbility => ({
  effects: [{ k: 'summonMinions', monsters, count, vis: 'warpPuff' }],
  range: 12,
  cd: 10,
  cast: 0.8,
  recover: 0.4,
  weight: 2,
  anim: 'cast',
  sfx: 'cast',
  ...o,
});

const blink = (dist: number, o: AbilityOpts = {}): MonsterAbility => ({
  effects: [{ k: 'blink', maxDist: dist, vis: 'warpPuff' }],
  range: 14,
  minRange: 3,
  cd: 4,
  cast: 0.2,
  recover: 0.1,
  weight: 1,
  anim: 'cast',
  sfx: 'warp',
  ...o,
});

const dash = (dist: number, mult: number, o: AbilityOpts = {}): MonsterAbility => ({
  effects: [{ k: 'dash', speed: 16, maxDist: dist, mult, el: 'phys', knock: 1.5, vis: 'shockwave' }],
  range: dist,
  minRange: 3,
  cd: 5,
  cast: 0.55,
  recover: 0.4,
  weight: 2,
  anim: 'slam',
  sfx: 'charge',
  ...o,
});

const defs: MonsterDef[] = [
  // ─── Verdant Wilds ───
  { id: 'slime', name: 'Moss Slime', sprite: 'slime', radius: 0.42, hp: 12, dmg: 2.5, armor: 2, speed: 2.1, xp: 0.8, ai: 'melee', aggro: 9, abilities: [bite(1)], pack: [3, 5] },
  { id: 'wolf', name: 'Dire Wolf', sprite: 'wolf', radius: 0.4, hp: 11, dmg: 3, armor: 3, speed: 5.0, xp: 1, ai: 'skirmish', aggro: 12, abilities: [bite(1, { cd: 0.9, cast: 0.2 })], pack: [3, 6] },
  { id: 'shroom', name: 'Sporeling', sprite: 'shroom', radius: 0.38, hp: 12, dmg: 2.5, armor: 3, speed: 1.5, xp: 1.1, ai: 'ranged', aggro: 10, abilities: [shot('spit', 'poison', 6, 1, { status: { id: 'poison', dur: 3, dot: 0.2 } })], pack: [2, 4] },
  { id: 'goblin', name: 'Thorn Goblin', sprite: 'goblin', radius: 0.36, hp: 14, dmg: 3.2, armor: 4, speed: 3.6, xp: 1.1, ai: 'melee', aggro: 10, abilities: [bite(1, { cast: 0.25 })], pack: [3, 6] },

  // ─── Bone Crypt ───
  { id: 'skeleton', name: 'Rattling Dead', sprite: 'skeleton', radius: 0.36, hp: 13, dmg: 3.4, armor: 6, speed: 3.2, xp: 1, ai: 'melee', aggro: 11, res: { poison: 50 }, abilities: [bite(1)], pack: [3, 6] },
  { id: 'archer', name: 'Bone Archer', sprite: 'archer', radius: 0.36, hp: 10, dmg: 2.8, armor: 4, speed: 2.6, xp: 1.1, ai: 'ranged', aggro: 13, res: { poison: 50 }, abilities: [shot('arrow', 'phys', 13, 1, { cd: 1.6, cast: 0.45 })], pack: [2, 4] },
  { id: 'zombie', name: 'Rotting Husk', sprite: 'zombie', radius: 0.4, hp: 22, dmg: 4.2, armor: 2, speed: 1.6, xp: 1.2, ai: 'melee', aggro: 8, res: { poison: 75 }, abilities: [bite(1, { cd: 1.5, cast: 0.5 })], pack: [4, 7] },
  { id: 'ghost', name: 'Wailing Shade', sprite: 'ghost', radius: 0.36, hp: 10, dmg: 3.4, armor: 0, speed: 4.2, xp: 1.3, ai: 'skirmish', aggro: 12, fly: true, noCorpse: true, res: { phys: 30, cold: 50, poison: 100 }, abilities: [bite(1, { el: 'cold', status: { id: 'chill', dur: 2 }, vis: 'coldHit' })], pack: [2, 4] },
  { id: 'necro', name: 'Grave Priest', sprite: 'necro', radius: 0.36, hp: 16, dmg: 3.2, armor: 4, speed: 2.4, xp: 1.8, ai: 'summoner', aggro: 13, abilities: [shot('void', 'magic', 7, 1, { cd: 2.5 }), summon(['skeleton'], 2, { cd: 9 })], pack: [1, 2] },

  // ─── Mirage Dunes ───
  { id: 'scorpion', name: 'Dune Stinger', sprite: 'scorpion', radius: 0.42, hp: 15, dmg: 3.4, armor: 8, speed: 4, xp: 1.1, ai: 'melee', aggro: 10, res: { poison: 50 }, abilities: [bite(1, { el: 'poison', status: { id: 'poison', dur: 3, dot: 0.3 }, vis: 'poisonHit' })], pack: [3, 5] },
  { id: 'mummy', name: 'Sand Mummy', sprite: 'mummy', radius: 0.4, hp: 24, dmg: 4.2, armor: 4, speed: 2, xp: 1.3, ai: 'melee', aggro: 9, res: { fire: -25, poison: 75 }, abilities: [bite(1, { cd: 1.4, cast: 0.4 })], pack: [3, 6] },
  { id: 'wraith', name: 'Sunwraith', sprite: 'wraith', radius: 0.36, hp: 12, dmg: 3.2, armor: 0, speed: 3.6, xp: 1.4, ai: 'ranged', aggro: 13, fly: true, noCorpse: true, res: { fire: 60, phys: 20 }, abilities: [shot('firebolt', 'fire', 9, 1, { status: { id: 'burn', dur: 2, dot: 0.2 } })], pack: [2, 4] },
  { id: 'beetle', name: 'Blast Scarab', sprite: 'beetle', radius: 0.34, hp: 6, dmg: 5, armor: 2, speed: 5.5, xp: 0.9, ai: 'kamikaze', aggro: 12, noCorpse: true, deathBurst: { r: 2.4, mult: 1.3, el: 'fire', vis: 'explosion' }, abilities: [nova(1.8, 0, 'fire', 'none', { range: 1.1, delay: 0, cast: 0.1, cd: 1 })], pack: [3, 6] },

  // ─── Derelict Starship ───
  { id: 'drone', name: 'Security Drone', sprite: 'drone', radius: 0.34, hp: 11, dmg: 2.8, armor: 6, speed: 3.6, xp: 1.1, ai: 'ranged', aggro: 13, fly: true, noCorpse: true, res: { poison: 100, light: -25 }, abilities: [shot('laser', 'light', 15, 1, { cd: 1.4, cast: 0.3 })], pack: [3, 5] },
  { id: 'robot', name: 'Maintenance Unit', sprite: 'robot', radius: 0.42, hp: 22, dmg: 4.4, armor: 14, speed: 2.4, xp: 1.4, ai: 'melee', aggro: 10, noCorpse: true, res: { poison: 100, light: -25 }, abilities: [bite(1, { cd: 1.3, cast: 0.45 })], pack: [2, 4] },
  { id: 'turret', name: 'Auto-Turret', sprite: 'turret', radius: 0.42, hp: 16, dmg: 2.6, armor: 16, speed: 0, xp: 1.3, ai: 'turret', aggro: 13, rooted: true, noCorpse: true, res: { poison: 100, light: -25 }, abilities: [shot('bullet', 'phys', 14, 1, { cd: 0.8, cast: 0.15, range: 12 })], pack: [1, 2] },
  { id: 'xeno', name: 'Xeno Skitter', sprite: 'xeno', radius: 0.38, hp: 12, dmg: 3.6, armor: 4, speed: 5.4, xp: 1.1, ai: 'skirmish', aggro: 13, res: { poison: 50 }, abilities: [bite(1, { cd: 0.8, cast: 0.18 })], pack: [4, 7] },
  { id: 'spitter', name: 'Xeno Spitter', sprite: 'spitter', radius: 0.4, hp: 15, dmg: 3, armor: 6, speed: 2.4, xp: 1.3, ai: 'ranged', aggro: 12, res: { poison: 75 }, abilities: [shot('spit', 'poison', 8, 1, { status: { id: 'poison', dur: 3, dot: 0.3 } }), groundPatch(2, 4, 0.3, 'poison', 'poisonGround', { cd: 8 })], pack: [2, 3] },

  // ─── Prismarch Caverns ───
  { id: 'cgolem', name: 'Crystal Golem', sprite: 'golem', radius: 0.5, hp: 32, dmg: 5, armor: 18, speed: 2, xp: 1.7, ai: 'melee', aggro: 9, res: { cold: 30, light: 30, poison: 100 }, abilities: [bite(1, { cd: 1.6, cast: 0.5, reach: 1.8 }), nova(3, 1.1, 'phys', 'shockwave', { cd: 6, delay: 0.6, status: { id: 'stun', dur: 0.5 } })], pack: [1, 3] },
  { id: 'wisp', name: 'Volt Wisp', sprite: 'wisp', radius: 0.3, hp: 7, dmg: 4, armor: 0, speed: 5.5, xp: 0.9, ai: 'kamikaze', aggro: 13, fly: true, noCorpse: true, res: { light: 80 }, deathBurst: { r: 2.2, mult: 1.2, el: 'light', vis: 'sparkBurst' }, abilities: [nova(1.8, 0, 'light', 'none', { range: 1, delay: 0, cast: 0.1, cd: 1 })], pack: [3, 6] },
  { id: 'cspider', name: 'Geode Spider', sprite: 'spider', radius: 0.42, hp: 14, dmg: 3.2, armor: 8, speed: 4.6, xp: 1.2, ai: 'skirmish', aggro: 11, res: { poison: 30 }, abilities: [bite(1, { el: 'poison', status: { id: 'poison', dur: 3, dot: 0.25 }, vis: 'poisonHit' }), shot('shard', 'magic', 10, 0.8, { cd: 3, count: 3, spread: 30, weight: 1 })], pack: [3, 5] },
  { id: 'construct', name: 'Arcane Construct', sprite: 'construct', radius: 0.38, hp: 16, dmg: 3.2, armor: 10, speed: 2.2, xp: 1.5, ai: 'ranged', aggro: 14, fly: true, noCorpse: true, res: { poison: 100 }, abilities: [shot('arcane', 'magic', 8, 1, { homing: 2, cd: 1.9, life: 2.2 }), nova(3, 0, 'magic', 'none', { weight: 0 })], pack: [2, 3] },

  // ─── Neon Sprawl ───
  { id: 'thug', name: 'Syndicate Thug', sprite: 'thug', radius: 0.38, hp: 15, dmg: 3, armor: 8, speed: 3.2, xp: 1.2, ai: 'ranged', aggro: 13, abilities: [shot('bullet', 'phys', 18, 1, { cd: 1.2, cast: 0.25 })], pack: [3, 5] },
  { id: 'ninja', name: 'Chrome Ninja', sprite: 'ninja', radius: 0.36, hp: 14, dmg: 4.2, armor: 6, speed: 5.2, xp: 1.5, ai: 'skirmish', aggro: 14, abilities: [bite(1, { cd: 0.9, cast: 0.2, el: 'phys', vis: 'slashWhite' }), blink(7, { cd: 5 })], pack: [2, 4] },
  { id: 'hovercop', name: 'Hover Enforcer', sprite: 'hovercop', radius: 0.42, hp: 20, dmg: 3.6, armor: 12, speed: 2.8, xp: 1.5, ai: 'ranged', aggro: 14, fly: true, noCorpse: true, res: { poison: 100 }, abilities: [shot('plasma', 'light', 11, 1, { cd: 1.9 }), shot('rocket', 'fire', 7, 0.7, { cd: 6, homing: 2, explode: { r: 2, mult: 1, el: 'fire', vis: 'explosion' }, life: 2.6, weight: 1 })], pack: [1, 3] },
  { id: 'cyborg', name: 'Augmented Bruiser', sprite: 'cyborg', radius: 0.46, hp: 30, dmg: 5, armor: 16, speed: 2.6, xp: 1.6, ai: 'melee', aggro: 10, res: { poison: 30, light: -20 }, abilities: [bite(1, { cd: 1.3, cast: 0.45 }), dash(8, 1.3, { cd: 7 })], pack: [1, 3] },

  // ─── Frostfell ───
  { id: 'icewolf', name: 'Rime Wolf', sprite: 'wolf', pal: { a: '#3cbcfc', b: '#a4e4fc', c: '#fcfcfc', d: '#0058f8' }, radius: 0.42, hp: 15, dmg: 3.8, armor: 8, speed: 5.4, xp: 1.2, ai: 'skirmish', aggro: 13, res: { cold: 60, fire: -30 }, abilities: [bite(1, { el: 'cold', cd: 0.9, cast: 0.2, status: { id: 'chill', dur: 1.5 }, vis: 'coldHit' })], pack: [3, 6] },
  { id: 'yeti', name: 'Frost Yeti', sprite: 'yeti', radius: 0.52, hp: 36, dmg: 5.4, armor: 12, speed: 3, xp: 1.8, ai: 'melee', aggro: 10, res: { cold: 60, fire: -30 }, abilities: [bite(1, { cd: 1.4, cast: 0.4, reach: 1.9 }), nova(3.5, 1, 'cold', 'frostnova', { cd: 7, delay: 0.7, status: { id: 'chill', dur: 3 } })], pack: [1, 3] },
  { id: 'fwraith', name: 'Blizzard Shade', sprite: 'ghost', pal: { a: '#0058f8', b: '#3cbcfc', c: '#fcfcfc', d: '#a4e4fc' }, radius: 0.36, hp: 14, dmg: 3.6, armor: 0, speed: 3.6, xp: 1.4, ai: 'ranged', aggro: 13, fly: true, noCorpse: true, res: { cold: 90, fire: -30, phys: 25 }, abilities: [shot('icebolt', 'cold', 9, 1, { status: { id: 'chill', dur: 2 }, count: 3, spread: 28, cd: 2.4 })], pack: [2, 4] },
  { id: 'icemage', name: 'Rimebound Sorcerer', sprite: 'icemage', radius: 0.38, hp: 18, dmg: 3.6, armor: 6, speed: 2.6, xp: 1.7, ai: 'summoner', aggro: 13, res: { cold: 75, fire: -25 }, abilities: [shot('icebolt', 'cold', 9, 1, { cd: 2.2, status: { id: 'chill', dur: 2 } }), nova(3, 1.5, 'cold', 'frostnova', { at: 'target', delay: 1, cd: 5, status: { id: 'freeze', dur: 0.8 } })], pack: [1, 2] },

  // ─── The Warp Maw ───
  { id: 'imp', name: 'Warp Imp', sprite: 'imp', radius: 0.34, hp: 12, dmg: 3.2, armor: 4, speed: 4.4, xp: 1.2, ai: 'ranged', aggro: 13, res: { fire: 50 }, abilities: [shot('fireball', 'fire', 10, 1, { cd: 1.6, status: { id: 'burn', dur: 2, dot: 0.2 } }), blink(6, { cd: 6 })], pack: [3, 6] },
  { id: 'demon', name: 'Warp Reaver', sprite: 'demon', radius: 0.46, hp: 30, dmg: 5.4, armor: 12, speed: 3.6, xp: 1.7, ai: 'melee', aggro: 12, res: { fire: 40, magic: 20 }, abilities: [bite(1, { cd: 1.1, cast: 0.3, reach: 1.8 }), dash(8, 1.3, { cd: 7 })], pack: [2, 4] },
  { id: 'eye', name: 'Gazing Horror', sprite: 'eye', radius: 0.46, hp: 20, dmg: 3.8, armor: 6, speed: 2, xp: 1.8, ai: 'ranged', aggro: 15, fly: true, noCorpse: true, res: { magic: 40, poison: 100 }, abilities: [shot('void', 'magic', 10, 1, { cd: 1.7 }), shot('void', 'magic', 7, 0.7, { ring: true, count: 10, cd: 7, weight: 1, range: 7 })], pack: [1, 3] },
  { id: 'warpling', name: 'Warpling', sprite: 'warpling', radius: 0.34, hp: 8, dmg: 5, armor: 2, speed: 5.5, xp: 1, ai: 'kamikaze', aggro: 14, noCorpse: true, res: { magic: 40 }, deathBurst: { r: 2.5, mult: 1.3, el: 'magic', vis: 'voidBurst' }, abilities: [nova(1.8, 0, 'magic', 'none', { range: 1, delay: 0, cast: 0.1, cd: 1 })], pack: [3, 7] },

  // ─── BOSSES ───
  {
    id: 'b_treant', name: 'Elderbark, the Rotted King', sprite: 'b_treant', big: true, radius: 1.0, hp: 170, dmg: 3.2, armor: 10, speed: 1.9, xp: 30, ai: 'boss', aggro: 18,
    say: 'THE ROOTS REMEMBER. THE ROOTS HUNGER.',
    res: { fire: -30, poison: 50 },
    abilities: [
      bite(1.1, { cd: 1.3, cast: 0.45, reach: 2.6, weight: 4 }),
      nova(5, 1.2, 'poison', 'poisonGround', { at: 'self', delay: 1.0, cd: 6, status: { id: 'poison', dur: 3, dot: 0.2 } }),
      shot('spit', 'poison', 7, 0.9, { ring: true, count: 12, cd: 7, range: 9, status: { id: 'poison', dur: 2, dot: 0.15 } }),
    ],
    phases: [{ below: 0.6, monsters: ['goblin', 'slime'], count: 5, say: 'CHILDREN! RISE AND FEED!' }, { below: 0.3, monsters: ['wolf'], count: 6, say: 'THE WILD ANSWERS ME!' }],
  },
  {
    id: 'b_lich', name: 'Morvane the Hollow Lich', sprite: 'b_lich', big: true, radius: 0.9, hp: 200, dmg: 3.6, armor: 12, speed: 2.4, xp: 36, ai: 'boss', aggro: 20,
    say: 'YOUR BONES WILL SERVE ME WELL.',
    res: { poison: 90, cold: 40 },
    abilities: [
      shot('void', 'magic', 9, 1, { count: 3, spread: 30, cd: 1.8, weight: 4, range: 13 }),
      nova(3.5, 1.5, 'cold', 'frostnova', { at: 'target', delay: 1.0, cd: 5, status: { id: 'freeze', dur: 0.8 } }),
      summon(['skeleton', 'archer'], 4, { cd: 14 }),
      blink(10, { cd: 7, minRange: 2 }),
    ],
    phases: [{ below: 0.5, monsters: ['zombie', 'ghost'], count: 6, say: 'RISE! RISE, ALL OF YOU!' }],
  },
  {
    id: 'b_djinn', name: 'Azhar, Sultan of Glass', sprite: 'b_djinn', big: true, radius: 0.95, hp: 240, dmg: 4, armor: 10, speed: 3.4, xp: 42, ai: 'boss', aggro: 20, fly: true,
    say: 'YOU WISH TO PASS? THEN WISH.',
    res: { fire: 60, poison: 50 },
    abilities: [
      shot('fireball', 'fire', 9, 1, { count: 5, spread: 60, cd: 2.2, weight: 4, range: 13, status: { id: 'burn', dur: 2, dot: 0.2 } }),
      groundPatch(3.5, 5, 0.4, 'fire', 'fireGround', { cd: 7 }),
      dash(10, 1.5, { cd: 6, weight: 2 }),
      nova(5, 1.8, 'fire', 'explosion', { at: 'self', delay: 1.1, cd: 8 }),
    ],
    phases: [{ below: 0.5, monsters: ['wraith', 'beetle'], count: 6, say: 'THE SANDS BURN WITH MY WRATH!' }],
  },
  {
    id: 'b_mech', name: 'OVERSEER-9', sprite: 'b_mech', big: true, radius: 1.1, hp: 290, dmg: 4.4, armor: 24, speed: 2.2, xp: 48, ai: 'boss', aggro: 22, noCorpse: true,
    say: 'INTRUDERS DETECTED. INITIATING PURGE PROTOCOL.',
    res: { poison: 100, light: -20 },
    abilities: [
      shot('bullet', 'phys', 17, 0.55, { count: 7, spread: 44, cd: 1.5, cast: 0.3, weight: 4, range: 13 }),
      shot('rocket', 'fire', 8, 0.6, { count: 3, spread: 40, homing: 2.2, cd: 5, life: 2.6, explode: { r: 2.6, mult: 1.1, el: 'fire', vis: 'explosion' } }),
      nova(4, 2, 'light', 'orbital', { at: 'target', delay: 1.3, cd: 6 }),
      dash(12, 1.5, { cd: 8 }),
    ],
    phases: [{ below: 0.66, monsters: ['drone'], count: 4, say: 'DEPLOYING SUPPORT UNITS.' }, { below: 0.33, monsters: ['robot', 'drone'], count: 6, say: 'WARNING: CORE EXPOSED. ALL UNITS RESPOND.' }],
  },
  {
    id: 'b_wyrm', name: 'Prismarch, the Faceted Wyrm', sprite: 'b_wyrm', big: true, radius: 1.15, hp: 340, dmg: 4.6, armor: 18, speed: 3, xp: 56, ai: 'boss', aggro: 22,
    say: 'SHATTER. SHATTER AND SHINE.',
    res: { poison: 60, light: 30, cold: 30, fire: 30 },
    abilities: [
      shot('shard', 'magic', 11, 0.8, { count: 9, spread: 90, cd: 2, weight: 4, range: 12 }),
      shot('spark', 'light', 8, 0.7, { ring: true, count: 18, cd: 6, range: 10 }),
      nova(4, 2, 'light', 'sparkBurst', { at: 'target', delay: 1.0, cd: 5, status: { id: 'shock', dur: 2 } }),
      dash(12, 1.5, { cd: 7 }),
    ],
    phases: [{ below: 0.6, monsters: ['cgolem'], count: 2, say: 'SIBLINGS. AWAKEN.' }, { below: 0.3, monsters: ['wisp'], count: 8, say: 'BURN BRIGHT, LITTLE LIGHTS.' }],
  },
  {
    id: 'b_queen', name: 'Vex, the Chrome Queen', sprite: 'b_queen', big: true, radius: 0.9, hp: 390, dmg: 5, armor: 20, speed: 3.8, xp: 66, ai: 'boss', aggro: 22,
    say: 'YOU ARE ON THE WRONG SIDE OF THE CONTRACT.',
    res: { poison: 60 },
    abilities: [
      bite(1.3, { cd: 0.9, cast: 0.25, reach: 2.2, weight: 3, vis: 'slashWhite' }),
      shot('plasma', 'light', 13, 0.9, { count: 5, spread: 40, cd: 1.8, weight: 3, range: 12 }),
      blink(10, { cd: 4, weight: 2, minRange: 1 }),
      nova(3.5, 1.9, 'light', 'orbital', { at: 'target', delay: 0.9, cd: 4 }),
    ],
    phases: [{ below: 0.66, monsters: ['thug', 'ninja'], count: 5, say: 'SECURITY! EARN YOUR PAY!' }, { below: 0.33, monsters: ['hovercop', 'ninja'], count: 6, say: 'ALL ASSETS. NOW.' }],
  },
  {
    id: 'b_titan', name: 'Ymirax the Glacier Titan', sprite: 'b_titan', big: true, radius: 1.25, hp: 460, dmg: 5.6, armor: 24, speed: 2.2, xp: 76, ai: 'boss', aggro: 22,
    say: 'THE LONG WINTER DOES NOT END.',
    res: { cold: 80, fire: -30, poison: 50 },
    abilities: [
      bite(1.4, { cd: 1.5, cast: 0.55, reach: 3, weight: 3, el: 'cold', vis: 'coldHit', status: { id: 'chill', dur: 2 } }),
      nova(7, 1.6, 'cold', 'frostnova', { at: 'self', delay: 1.3, cd: 7, status: { id: 'freeze', dur: 1 } }),
      shot('icebolt', 'cold', 9, 0.9, { ring: true, count: 16, cd: 5, range: 11, status: { id: 'chill', dur: 2 } }),
      groundPatch(3.5, 6, 0.5, 'cold', 'iceGround', { cd: 8, status: { id: 'slow', dur: 1 } }),
    ],
    phases: [{ below: 0.5, monsters: ['icewolf', 'yeti'], count: 5, say: 'PACK OF THE ETERNAL NIGHT, HUNT!' }],
  },
  {
    id: 'b_tyrant', name: 'THE WARP TYRANT', sprite: 'b_tyrant', big: true, radius: 1.3, hp: 620, dmg: 6.2, armor: 28, speed: 2.8, xp: 120, ai: 'boss', aggro: 26, fly: true,
    say: 'I AM THE SEAM BETWEEN WORLDS. KNEEL.',
    res: { fire: 25, cold: 25, light: 25, poison: 50, magic: 25 },
    abilities: [
      shot('void', 'magic', 11, 0.8, { count: 7, spread: 70, cd: 1.6, weight: 4, range: 14 }),
      shot('fireball', 'fire', 8, 0.7, { ring: true, count: 20, cd: 5, range: 12 }),
      nova(4.5, 2.2, 'magic', 'voidBurst', { at: 'target', delay: 1.1, cd: 4.5 }),
      { effects: [{ k: 'ground', r: 7, dur: 4, every: 0.4, mult: 0.35, el: 'magic', at: 'self', pull: 3, vis: 'singularity' }], range: 9, cd: 15, cast: 0.8, recover: 0.5, weight: 2, anim: 'cast', sfx: 'warp' },
      blink(12, { cd: 7, minRange: 2 }),
    ],
    phases: [
      { below: 0.75, monsters: ['imp', 'warpling'], count: 7, say: 'BEHOLD MY LEGIONS.' },
      { below: 0.5, monsters: ['demon', 'eye'], count: 5, say: 'YOU ARE BECOMING TIRESOME.' },
      { below: 0.25, monsters: ['warpling', 'demon', 'imp'], count: 9, say: 'THE MAW OPENS! ALL WORLDS WILL DROWN!' },
    ],
  },

  // ─── SUMMONS (player side) ───
  { id: 's_skeleton', name: 'Skeleton', sprite: 'skeleton', pal: { d: '#58f898' }, radius: 0.34, hp: 1, dmg: 1, armor: 8, speed: 4.6, xp: 0, ai: 'melee', aggro: 11, noCorpse: true, abilities: [bite(1, { cd: 0.9, cast: 0.2 })] },
  { id: 's_ghoul', name: 'Ghoul', sprite: 'zombie', pal: { a: '#006800', b: '#58d854', c: '#b8f818' }, radius: 0.34, hp: 1, dmg: 1, armor: 4, speed: 5.4, xp: 0, ai: 'melee', aggro: 14, noCorpse: true, abilities: [bite(1, { cd: 0.7, cast: 0.15 })] },
  { id: 's_golem', name: 'Bone Golem', sprite: 'golem', pal: { a: '#7c7c7c', b: '#bcbcbc', c: '#fcfcfc', d: '#58f898' }, radius: 0.56, hp: 1, dmg: 1, armor: 24, speed: 4, xp: 0, ai: 'melee', aggro: 11, noCorpse: true, abilities: [bite(1, { cd: 1.1, cast: 0.3, reach: 2 }), nova(3.5, 1.2, 'phys', 'shockwave', { cd: 6, delay: 0.2 })] },
  { id: 's_turret', name: 'Sentry', sprite: 'turret', pal: { a: '#887000', b: '#f8b800', c: '#fcfcfc', d: '#f83800' }, radius: 0.42, hp: 1, dmg: 1, armor: 18, speed: 0, xp: 0, ai: 'turret', aggro: 13, rooted: true, noCorpse: true, abilities: [shot('bullet', 'phys', 22, 1, { cd: 0.42, cast: 0.05, range: 13, recover: 0.05 })] },
];

export const MONSTERS: Record<string, MonsterDef> = Object.fromEntries(defs.map((m) => [m.id, m]));

// ─── Champion & unique modifiers ───

export interface EliteAffix {
  id: string;
  name: string;
  hp?: number;
  dmg?: number;
  speed?: number;
  armor?: number;
  /** Explodes on death. */
  burst?: { r: number; mult: number; el: Element; vis: string };
  /** Adds an extra attack effect. */
  ability?: MonsterAbility;
  /** Applied to the targets it hits. */
  status?: StatusApply;
  steal?: number;
  thorns?: number;
  color: string;
}

export const ELITE_AFFIXES: EliteAffix[] = [
  { id: 'fast', name: 'Extra Fast', speed: 1.5, color: '#f8d878' },
  { id: 'strong', name: 'Extra Strong', dmg: 1.5, color: '#f83800' },
  { id: 'stone', name: 'Stone Skin', armor: 3, hp: 1.3, color: '#bcbcbc' },
  { id: 'fire', name: 'Fire Enchanted', burst: { r: 3, mult: 1.6, el: 'fire', vis: 'explosion' }, ability: nova(3, 0.9, 'fire', 'explosion', { delay: 0.7, cd: 6 }), color: '#f87858' },
  { id: 'cold', name: 'Cold Enchanted', status: { id: 'chill', dur: 2 }, ability: nova(3.5, 0.8, 'cold', 'frostnova', { delay: 0.7, cd: 7, status: { id: 'chill', dur: 3 } }), color: '#3cbcfc' },
  { id: 'light', name: 'Lightning Enchanted', burst: { r: 2.6, mult: 1.3, el: 'light', vis: 'sparkBurst' }, ability: shot('spark', 'light', 9, 0.6, { ring: true, count: 8, cd: 5, range: 7 }), color: '#fce0a8' },
  { id: 'vamp', name: 'Vampiric', steal: 0.5, hp: 1.2, color: '#d800cc' },
  { id: 'thorn', name: 'Spectral Hit', thorns: 0.25, color: '#6844fc' },
  { id: 'tele', name: 'Teleporting', ability: blink(8, { cd: 5, minRange: 2 }), color: '#58f898' },
];

const UNIQUE_PREFIX = ['Bone', 'Rot', 'Grim', 'Blood', 'Ash', 'Doom', 'Foul', 'Gore', 'Storm', 'Night', 'Plague', 'Wrath', 'Hex', 'Skull', 'Dread', 'Warp'];
const UNIQUE_SUFFIX = ['fang', 'maw', 'gut', 'bane', 'rip', 'hide', 'claw', 'brand', 'shard', 'howl', 'tooth', 'spawn'];
const UNIQUE_TITLE = ['the Hungry', 'the Cold', 'the Mad', 'the Pale', 'Skullsplitter', 'the Unclean', 'Flesh-Eater', 'the Forsaken', 'the Wretched', 'Doomcaller', 'the Twice-Dead', 'Warpborn'];

export function uniqueName(r: { pick<T>(a: readonly T[]): T }): string {
  return `${r.pick(UNIQUE_PREFIX)}${r.pick(UNIQUE_SUFFIX)} ${r.pick(UNIQUE_TITLE)}`;
}

export type { EffectDef };
